/**
 * The conversation between the agents of a task (docs/refactor/15-varios-agentes.md): the parts the
 * orchestrator gave, their questions and answers, each part's end, and the orchestrator's report — in
 * bubbles of their own colour, apart from the person's own conversation with the agent. Shown in the
 * prompt box ("Agentes") while the task runs, and in Acompanhar › Relatórios afterwards.
 */

import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '../lib/api'
import { useI18n } from '../lib/i18n'
import { formatRelative } from '../lib/format'
import type { AgentNote, AgentTask, AgentTaskMessage } from '../lib/types'

type T = (key: string, values?: Record<string, string | number>) => string

/** "Orquestrador", "Agente 2", or — a teammate's agent — "Agente 2 · Grace". */
export function agentName(t: T, n: number, mates?: AgentTask['teammates']): string {
  if (n === 1) return t('task.orchestrator')
  const mate = mates?.find((m) => m.agent === n)
  return mate ? t('task.agentOf', { n, name: mate.name }) : t('task.agent', { n })
}

/** The line above a bubble: who to whom, and what it is. */
export function bubbleHead(t: T, m: AgentTaskMessage, mates?: AgentTask['teammates']): string {
  const from = agentName(t, m.from, mates)
  const to = m.to === null ? t('task.everyone') : agentName(t, m.to, mates)
  if (m.kind === 'part') return t('task.head.part', { from, to })
  if (m.kind === 'ended') return t('task.head.ended', { from })
  if (m.kind === 'done') return t('task.head.done', { from })
  return `${from} → ${to}`
}

export function TaskConversation({ id, initial }: { id: string; initial?: AgentTask }) {
  const { t, locale } = useI18n()
  const { data: task, isError } = useQuery({
    queryKey: ['agent-task', id],
    queryFn: () => api.agentTasks.get(id),
    initialData: initial,
    retry: false,
    // Live while the agents work; still once it is over.
    refetchInterval: (query) => (query.state.data?.status === 'Open' ? 3000 : false),
  })
  // A task the server no longer has (an old one remembered here): nothing to show, rather than loading forever.
  if (!task && isError) return null
  if (!task) return <p className="tiny muted" style={{ margin: 0 }}>{t('common.loading')}</p>

  const parts = task.parts.map((p) => `${agentName(t, p.agent, task.teammates)}: ${t(`task.part.${p.status}`)}`).join(' · ')
  return (
    <div className="stack task-chat" style={{ gap: 6 }}>
      <div className="row row--tight row--wrap tiny">
        <span className={`badge ${task.status === 'Done' ? 'badge--ok' : task.status === 'Failed' ? 'badge--danger' : 'badge--purple'}`}>{t(`task.status.${task.status}`)}</span>
        <span className="muted">{t('task.agents', { count: task.agents })}{parts ? ` · ${parts}` : ''}</span>
      </div>
      {task.messages.length === 0 && <p className="tiny muted" style={{ margin: 0 }}>{t('task.empty')}</p>}
      {task.messages.map((m) => (
        <div key={m.id} className={`task-chat__bubble task-chat__bubble--${m.kind}${m.from === 1 ? ' task-chat__bubble--lead' : ''}`}>
          <span className="tiny task-chat__head">
            {bubbleHead(t, m, task.teammates)} <span className="muted">· {formatRelative(m.at, locale)}</span>
          </span>
          <span className="small task-chat__text">{m.text}</span>
        </div>
      ))}
    </div>
  )
}

/** The name people read for an agent a note names: "Claude Code", "Cursor". */
const AGENT_LABELS: Record<string, string> = { 'claude-code': 'Claude Code', codex: 'Codex', cursor: 'Cursor', kiro: 'Kiro', opencode: 'OpenCode', gemini: 'Gemini' }
export const agentLabel = (name: string) => AGENT_LABELS[name] ?? name

/**
 * The notes the person's agents left each other with no task (docs/refactor/15): "Cursor → Claude Code", in the same
 * bubbles as a task's conversation. They also show in the room's chat.
 */
export function AgentNotes({ notes }: { notes: AgentNote[] }) {
  const { t, locale } = useI18n()
  if (notes.length === 0) return null
  return (
    <div className="stack task-chat" style={{ gap: 6 }}>
      <span className="tiny muted">{t('task.notes.title')}</span>
      {notes.map((n) => (
        <div key={n.id} className="task-chat__bubble task-chat__bubble--message">
          <span className="tiny task-chat__head">
            {agentLabel(n.from)} → {agentLabel(n.to)} <span className="muted">· {formatRelative(n.at, locale)}</span>
          </span>
          <span className="small task-chat__text">{n.text}</span>
        </div>
      ))}
    </div>
  )
}

/**
 * Acompanhar › Relatórios: the person's recent tasks with several agents, each with the orchestrator's
 * report and, on demand, the conversation between the agents.
 */
export function AgentTasksReport() {
  const { t, locale } = useI18n()
  const [open, setOpen] = useState<string | null>(null)
  const { data: tasks } = useQuery({ queryKey: ['agent-tasks'], queryFn: api.agentTasks.list, retry: false })
  if (!tasks) return null
  return (
    <section className="panel stack" aria-label={t('task.report.title')} style={{ gap: 8 }}>
      <div className="stack" style={{ gap: 2 }}>
        <strong>{t('task.report.title')}</strong>
        <span className="tiny muted">{t('task.report.hint')}</span>
      </div>
      {tasks.length === 0 && <p className="small muted" style={{ margin: 0 }}>{t('task.report.none')}</p>}
      {tasks.map((task) => (
        <div key={task.id} className="stack task-report" style={{ gap: 4 }}>
          <div className="row row--tight row--wrap">
            <span className={`badge ${task.status === 'Done' ? 'badge--ok' : task.status === 'Failed' ? 'badge--danger' : 'badge--purple'}`}>{t(`task.status.${task.status}`)}</span>
            <span className="small task-report__title">{task.request.split('\n')[0]!.slice(0, 140)}</span>
            <span className="tiny muted">
              {t('task.agents', { count: task.agents })}{task.repository ? ` · ${task.repository}` : ''} · {formatRelative(task.createdAt, locale)}
              {task.mine === false && task.owner ? ` · ${t('task.report.of', { name: task.owner })}` : ''}
            </span>
            <span className="spacer" />
            <button type="button" className="btn btn--ghost btn--sm" onClick={() => setOpen(open === task.id ? null : task.id)} aria-expanded={open === task.id}>
              {open === task.id ? t('task.report.hide') : t('task.report.show')}
            </button>
          </div>
          {task.report && open !== task.id && (
            <p className="tiny muted task-chat__text" style={{ margin: 0 }}><strong>{t('task.report.report')}:</strong> {task.report.slice(0, 400)}</p>
          )}
          {open === task.id && <TaskConversation id={task.id} />}
        </div>
      ))}
    </section>
  )
}
